import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

function SelectedCriteria() {
  const [criteria, setCriteria] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCriteria = async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) {
        setIsLoading(false);
        return;
      }

      try {
        const docSnap = await getDoc(doc(db, "profiles", uid));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setCriteria(data);
        } else {
          console.warn("Profile not found.");
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCriteria();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const id = requestAnimationFrame(() => setMounted(true));
      return () => cancelAnimationFrame(id);
    }
  }, [isLoading]);

  // Format aggregate marks display
  const getAggregateMarksDisplay = () => {
    if (criteria?.obtainedMarks && criteria?.maximumMarks) {
      const percentage = criteria?.aggregatePercentage || 
        ((parseFloat(criteria.obtainedMarks) / parseFloat(criteria.maximumMarks)) * 100).toFixed(2);
      return `${criteria.obtainedMarks}/${criteria.maximumMarks} (${percentage}%)`;
    }
    return null;
  };

  const criteriaItems = [
    {
      icon: "🌍",
      label: "Country of Graduation",
      value: criteria?.country,
      color: "blue"
    },
    {
      icon: "📚",
      label: "Aggregate Marks (4 Years)",
      value: getAggregateMarksDisplay(),
      color: "emerald"
    },
    {
      icon: "🎓",
      label: "Time Since Graduation",
      value: criteria?.graduationTime,
      color: "teal"
    },
    {
      icon: "📝",
      label: "USMLE Step 1",
      value: criteria?.step1,
      color: "indigo"
    },
    {
      icon: "📊",
      label: "USMLE Step 2",
      value: criteria?.step2,
      color: "purple"
    },
    {
      icon: "🏥",
      label: "U.S. Clinical Experience",
      value: criteria?.usce,
      color: "green"
    },
    {
      icon: "📄",
      label: "Visa Sponsorship",
      value: criteria?.visa,
      color: "orange"
    }
  ];

  const getCardClasses = (isSet) =>
    isSet
      ? "bg-white dark:bg-gray-700/80 border border-black/8 dark:border-gray-600 border-l-2 border-l-black/20 dark:border-l-gray-500 text-black dark:text-white hover:shadow-sm dark:hover:bg-gray-600/80"
      : "bg-gray-50/80 dark:bg-gray-800/80 border border-black/6 dark:border-gray-700 text-gray-500 dark:text-gray-400";

  const getCompletionPercentage = () => {
    const totalFields = criteriaItems.length;
    const completedFields = criteriaItems.filter(item => item.value && item.value !== "Not set").length;
    return Math.round((completedFields / totalFields) * 100);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-200 dark:border-gray-600 border-t-gray-500 dark:border-t-gray-400 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-[15px] text-gray-500 dark:text-gray-400">Loading your criteria...</p>
        </div>
      </div>
    );
  }

  if (!criteria) {
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 bg-black/[0.06] dark:bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">📋</span>
        </div>
        <h3 className="text-[20px] font-semibold text-black dark:text-white mb-2 tracking-[-0.02em]">No Criteria Set</h3>
        <p className="text-[15px] text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
          Set your preferences to get personalized recommendations.
        </p>
        <button
          onClick={() => navigate("/setup-profile")}
          className="bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black px-6 py-3 rounded-full text-[15px] font-medium transition-colors duration-200"
        >
          Set Up Profile
        </button>
      </div>
    );
  }

  const completionPercentage = getCompletionPercentage();

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div
        className="flex items-center justify-between flex-wrap gap-4"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}
      >
        <div>
          <h2 className="text-[20px] font-semibold text-black dark:text-white mb-1 tracking-[-0.02em]">Your Selection Criteria</h2>
          <p className="text-[14px] text-gray-500 dark:text-gray-400">Review and manage your residency program preferences</p>
        </div>
        <button
          onClick={() => navigate("/setup-profile")}
          className="border border-black/15 dark:border-gray-600 hover:border-black/25 dark:hover:border-gray-500 text-black dark:text-white px-4 py-2.5 rounded-full text-[13px] font-medium transition-all duration-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 active:scale-[0.96]"
        >
          Edit Criteria
        </button>
      </div>

      {/* Progress Section */}
      <div
        className="bg-black/[0.02] dark:bg-white/5 rounded-2xl p-6 border border-black/8 dark:border-gray-700"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.07s, transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.07s'
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-[15px] font-semibold text-black dark:text-white tracking-[-0.01em]">Profile Completion</h3>
            <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
              {completionPercentage}% of your criteria have been set
            </p>
          </div>
          <div className="text-2xl">{completionPercentage === 100 ? "🎉" : "⚡"}</div>
        </div>
        <div className="w-full bg-black/10 dark:bg-white/20 rounded-full h-2 overflow-hidden">
          <div
            className="profile-completion-bar-fill h-2 rounded-full min-w-0"
            style={{
              width: mounted ? `${completionPercentage}%` : '0%',
              transition: 'width 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s'
            }}
          />
        </div>
        {completionPercentage < 100 && (
          <p className="text-[13px] text-gray-600 dark:text-gray-400 mt-3">
            Complete your profile to get better college recommendations
          </p>
        )}
      </div>

      {/* Criteria Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {criteriaItems.map((item, index) => {
          const isSet = item.value && item.value !== "Not set";
          return (
            <div
              key={index}
              className={`p-4 rounded-xl border ${getCardClasses(isSet)}`}
              style={{
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(10px)',
                transition: `opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${0.14 + index * 0.05}s, transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${0.14 + index * 0.05}s, box-shadow 0.2s ease`
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center flex-1">
                  <span className="text-xl mr-3 flex-shrink-0">{item.icon}</span>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-[13px] mb-1 text-black dark:text-white">{item.label}</h4>
                    <p className={`font-medium text-[14px] break-words ${isSet ? "text-gray-800 dark:text-gray-200" : "text-gray-400 dark:text-gray-500"}`}>
                      {item.value || "Not set"}
                    </p>
                    {item.label === "Aggregate Marks (4 Years)" && item.value && (
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center space-x-4 text-[12px] text-gray-500 dark:text-gray-400">
                          <span>Obtained: {criteria?.obtainedMarks}</span>
                          <span>Maximum: {criteria?.maximumMarks}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-[11px] text-gray-600 dark:text-gray-400 bg-black/[0.08] dark:bg-white/10 px-2 py-1 rounded-full">
                            Cannot be modified
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ml-3 ${
                    isSet ? "bg-black/40 dark:bg-white/40" : "bg-gray-300 dark:bg-gray-600"
                  }`}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Section */}
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl border border-black/8 dark:border-gray-700 p-6"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.52s, transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.52s'
        }}
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-[15px] font-semibold text-black dark:text-white mb-1 tracking-[-0.01em]">Ready for Recommendations?</h3>
            <p className="text-[14px] text-gray-500 dark:text-gray-400 leading-relaxed">
              {completionPercentage >= 80
                ? "Your profile looks great! Explore college lists based on your criteria."
                : "Complete more criteria to get personalized college recommendations."}
            </p>
          </div>
          <div className="flex gap-3">
            {completionPercentage >= 80 && (
              <button
                onClick={() => navigate("/my-lists")}
                className="bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black px-5 py-2.5 rounded-full text-[14px] font-medium transition-all duration-200 active:scale-[0.96]"
              >
                View College Lists
              </button>
            )}
            <button
              onClick={() => navigate("/setup-profile")}
              className="border border-black/15 dark:border-gray-600 hover:border-black/25 dark:hover:border-gray-500 text-black dark:text-white bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 px-5 py-2.5 rounded-full text-[14px] font-medium transition-all duration-200 active:scale-[0.96]"
            >
              {completionPercentage >= 80 ? "Update Criteria" : "Complete Profile"}
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: '🎯', value: criteriaItems.filter(item => item.value && item.value !== "Not set").length, label: 'Criteria Set' },
          { icon: '📊', value: `${completionPercentage}%`, label: 'Complete' },
          { icon: '🏥', value: '600+', label: 'Programs Available' },
        ].map((stat, i) => (
          <div
            key={i}
            className="bg-black/[0.02] dark:bg-white/5 rounded-xl p-4 text-center border border-black/8 dark:border-gray-700 hover:shadow-sm dark:hover:bg-white/[0.07]"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(8px)',
              transition: `opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${0.58 + i * 0.06}s, transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${0.58 + i * 0.06}s, box-shadow 0.2s ease`
            }}
          >
            <div className="text-xl mb-2">{stat.icon}</div>
            <div className="text-[18px] font-semibold text-black dark:text-white">{stat.value}</div>
            <div className="text-[12px] text-gray-500 dark:text-gray-400">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SelectedCriteria;